import {
    Article,
    Book,
    ContentDetailType,
    ContentType,
    CreateContentBodyParams,
    ImportResponse,
    PodcastItem,
    Post,
    PostAttachment,
    PostAttachmentDetail,
    UrlParser,
    File as ImportFile,
    UserCoverStyle,
    Video,
    Visibility,
    CreateSourcePayload,
    WebsiteResponse,
    Website
} from "@authory-packages/core";
import { FinalImportResult } from "../components";
import { getSlug } from "../utils/getSlug";

const generateImageHTML = (srcImage: string) => `
    <figure>
        <img src="${srcImage}" />
        <figcaption></figcaption>
    </figure>
`;

/**
 * Shared logic between frontend and plugin to generate the content payload when creating content manually
 */
export const generateCreateContentPayload = async (
    formData: FinalImportResult,
    onCreateSourceHandler: (data: CreateSourcePayload) => Promise<WebsiteResponse>
): Promise<CreateContentBodyParams> => {
    let payloadSource = formData.sourceSlug!;

    // User opted to create a new source
    if (formData.newSource) {
        // Test for url validity, this will allows us to create online/offline sources
        let validUrl = true;
        let domain: string | null = null;

        try {
            const isArticleManualImportPath = formData.type === ContentType.Article;

            // we only allow creating a source with a url for the article flow and if it's not a snippet only domain
            // all other manual imports should instead create offline source
            // and thus we don't set a url or the active property
            if (formData.url && isArticleManualImportPath && !formData.isSnippetOnlydomain) {
                getSlug(formData.url);
            } else {
                validUrl = false;
            }

            // Save aliases on web flow AND submit domain only
            if (isArticleManualImportPath && formData.url && validUrl) {
                domain = UrlParser.extractDomain(formData.url);
            }
        } catch {
            validUrl = false;
        }

        const createSourceData = {
            active: false,
            publicationName: formData.name,
            url: validUrl ? (domain ? domain : formData.url) : undefined,
            importPastArticles: false,
            manualImportFlowTag: true
        };

        const newSource = await onCreateSourceHandler(createSourceData);

        payloadSource = newSource.canonicalSlug;
    }

    const params = new CreateContentBodyParams();

    if (formData.url) params.url = formData.url;
    if (formData.canonicalUrl) params.canonicalUrl = formData.canonicalUrl;

    const isPDF = formData.importResult?.type === ContentType.PdfFile;
    const isWebsite = formData.importResult?.type === ContentType.Website;
    const isEmail = formData.importResult?.type === ContentType.Email;
    const isImage = formData.importResult?.type === ContentType.Image;
    const isBook = formData.importResult?.type === ContentType.Book;
    const isCustomAudio = formData.importResult?.type === ContentType.Audio && formData.importResult?.content.type === ContentDetailType.CustomAudio;
    const isCustomVideo = formData.importResult?.type === ContentType.Video && formData.importResult?.content.type === ContentDetailType.CustomVideo;
    const isPublicType = isPDF || isWebsite || isEmail || isImage || isBook || isCustomAudio || isCustomVideo;

    if (isPublicType) {
        params.visibility = Visibility.Public;
    }

    // User set a default image and we had imported content - means he customized it.
    if (formData.importResult && formData.style === UserCoverStyle.image && !!formData.customImage?.length) {
        params.customThumbnailImage = formData.customImage;
    }

    // Generate content if not exists
    if (!formData.importResult) {
        formData.importResult = {} as ImportResponse;

        const addImg = formData.customImage ? generateImageHTML(formData.customImage) : "";

        if (formData.type === ContentType.TextPost) {
            formData.importResult.content = {
                html: `<p>${formData.description}</p>`,
                type: ContentDetailType.Post,
                by: "",
                attachment: formData.customImage
                    ? ({
                          type: PostAttachment.image,
                          src: formData.customImage
                      } as PostAttachmentDetail)
                    : []
            } as Post;
        } else if (formData.type === ContentType.PodcastItem) {
            formData.importResult.content = {
                coverImageUrl: null,
                html: `${addImg}<p>${formData.description}</p>`,
                type: ContentDetailType.PodcastItem
            } as PodcastItem;
        } else if (formData.type === ContentType.Video) {
            formData.importResult.content = {
                html: `${addImg}<p>${formData.description}</p>`,
                type: ContentDetailType.Video
            } as Video;
        } else if (formData.type === ContentType.Book) {
            formData.importResult.content = {
                html: `${addImg}<p>${formData.description}</p>`,
                type: ContentDetailType.Book
            } as Book;
        } else {
            formData.importResult.content = {
                html: `${addImg}<p>${formData.description}</p>`,
                type: ContentDetailType.Article
            } as Article;
        }
    }

    if (isPDF || isImage || isBook) {
        params.description = formData.description;
        (formData.importResult.content as ImportFile).html = formData.description;
    }

    if (isCustomAudio || isCustomVideo) {
        (formData.importResult.content as Video).html = `<p>${formData.description}</p>`;
    }

    if (formData.description && (isWebsite || isEmail)) {
        params.description = formData.description;
        (formData.importResult.content as Website).description = `<p>${formData.description.split("\n").join("<br />")}</p>`;
    }

    params.content = formData.importResult.content;
    params.type = formData.type;
    params.title = formData.title;
    params.source = payloadSource;
    params.contentAboutUser = formData.importResult.contentAboutUser;
    params.contentFromUser = formData.importResult.contentFromUser;

    if (formData.date) params.date = formData.date;

    return params;
};
